import { useEffect, useRef } from 'react';
import type * as React from 'react';

import { ApiError, type ApiClient } from '../api/client';
import {
  formatWebSocketDisconnectedMessage,
  formatWebSocketTokenFailureMessage
} from '../utils/errors';
import { compareConversationMessages } from '../utils/conversationRunActivity';
import { resolveInitialConversationModelId } from '../utils/conversationModelSelection';
import type { CodexRun, ConversationMessage, ConversationModelOption, QueueItem, RunEvent } from '../types';

type Args = {
  apiClient: ApiClient;
  appId: string;
  conversationId: string;
  conversationTitle: string | null | undefined;
  conversationAppId: string | null | undefined;
  authorizationReady: boolean;
  canLoadRuntime: boolean;
  onConversationAuthorizationLost: () => void;
  showConversationSecrets: boolean;
  streamingRun: CodexRun | null;
  selectedActivityMessageId: string | null;
  showRunnerSidebar: boolean;
  activityTreeByMessageId: Record<string, unknown>;
  activityTreeLoadingByMessageId: Record<string, boolean>;
  runByMessageId: Map<string, CodexRun>;
  messages: ConversationMessage[];
  models: ConversationModelOption[];
  selectedModelId: string | null;
  assistantText: string;
  activeOutputEventCount: number;
  activeUpdateEventCount: number;
  hasMoreMessages: boolean;
  loadingOlderMessages: boolean;
  resetConversationDerivedState: () => void;
  loadAll: () => Promise<void>;
  loadConversationSecrets: () => Promise<void>;
  loadRunActivityTree: (
    messageId: string,
    runId: string,
    force?: boolean,
    beforeEventId?: number | null
  ) => Promise<void>;
  loadOlderMessages: () => Promise<void>;
  runCursorRef: React.MutableRefObject<number | null>;
  liveStateRevisionRef: React.MutableRefObject<number>;
  threadRef: React.RefObject<HTMLDivElement | null>;
  threadNearBottomRef: React.MutableRefObject<boolean>;
  initialScrollDoneRef: React.MutableRefObject<boolean>;
  setError: (message: string | null) => void;
  setMessages: React.Dispatch<React.SetStateAction<ConversationMessage[]>>;
  setQueue: React.Dispatch<React.SetStateAction<QueueItem[]>>;
  setRuns: React.Dispatch<React.SetStateAction<CodexRun[]>>;
  setRunEvents: React.Dispatch<React.SetStateAction<RunEvent[]>>;
  setEventsError: React.Dispatch<React.SetStateAction<string | null>>;
  setQueueReorderBusy: React.Dispatch<React.SetStateAction<boolean>>;
  setShowConversationSecrets: React.Dispatch<React.SetStateAction<boolean>>;
  setConversationSecretDraftType: React.Dispatch<React.SetStateAction<'env' | 'file'>>;
  setConversationSecretDraftDescription: React.Dispatch<React.SetStateAction<string>>;
  setSelectedModelId: React.Dispatch<React.SetStateAction<string | null>>;
  setShellAutoExpandMessageId: React.Dispatch<React.SetStateAction<string | null>>;
  setStreamState: React.Dispatch<
    React.SetStateAction<'idle' | 'connecting' | 'live' | 'disconnected'>
  >;
  setShowJumpToLatest: React.Dispatch<React.SetStateAction<boolean>>;
  setAutopilotMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setExpandedRunnerSteps: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setApprovalDismissedKey: React.Dispatch<React.SetStateAction<string | null>>;
  setRunErrorDismissedId: React.Dispatch<React.SetStateAction<string | null>>;
  setSelectedActivityMessageId: React.Dispatch<React.SetStateAction<string | null>>;
};

function formatWebSocketClose(context: string, event?: CloseEvent, requestId?: string | null) {
  return formatWebSocketDisconnectedMessage({
    context,
    event,
    requestId,
    detail: 'Live updates are disconnected; refresh the page after the connection is restored.'
  });
}

function isConversationAuthorizationFailure(error: unknown): boolean {
  return error instanceof ApiError && [401, 403, 404].includes(error.status);
}

function isConversationAuthorizationClose(event: CloseEvent): boolean {
  // The backend explicitly uses WebSocket policy-violation (1008) when it
  // rechecks and finds access revoked. Do not classify ordinary 1006 network
  // drops as authorization losses; those retain the bounded reconnect policy.
  return event.code === 1008;
}

export function useConversationPageEffects({
  apiClient,
  appId,
  conversationId,
  conversationTitle,
  conversationAppId,
  authorizationReady,
  canLoadRuntime,
  onConversationAuthorizationLost,
  showConversationSecrets,
  streamingRun,
  selectedActivityMessageId,
  showRunnerSidebar,
  activityTreeByMessageId,
  activityTreeLoadingByMessageId,
  runByMessageId,
  messages,
  models,
  selectedModelId,
  assistantText,
  activeOutputEventCount,
  activeUpdateEventCount,
  hasMoreMessages,
  loadingOlderMessages,
  resetConversationDerivedState,
  loadAll,
  loadConversationSecrets,
  loadRunActivityTree,
  loadOlderMessages,
  runCursorRef,
  liveStateRevisionRef,
  threadRef,
  threadNearBottomRef,
  initialScrollDoneRef,
  setError,
  setMessages,
  setQueue,
  setRuns,
  setRunEvents,
  setEventsError,
  setQueueReorderBusy,
  setShowConversationSecrets,
  setConversationSecretDraftType,
  setConversationSecretDraftDescription,
  setSelectedModelId,
  setShellAutoExpandMessageId,
  setStreamState,
  setShowJumpToLatest,
  setAutopilotMenuOpen,
  setExpandedRunnerSteps,
  setApprovalDismissedKey,
  setRunErrorDismissedId,
  setSelectedActivityMessageId
}: Args) {
  const loadAllRef = useRef(loadAll);
  const loadConversationSecretsRef = useRef(loadConversationSecrets);
  const loadRunActivityTreeRef = useRef(loadRunActivityTree);
  const loadOlderMessagesRef = useRef(loadOlderMessages);
  const resetConversationDerivedStateRef = useRef(resetConversationDerivedState);
  const wsRef = useRef<WebSocket | null>(null);
  const wsRetryRef = useRef(0);
  const conversationWsRef = useRef<WebSocket | null>(null);
  const conversationWsRetryRef = useRef(0);
  const selectedActivityMessageIdRef = useRef(selectedActivityMessageId);
  useEffect(() => {
    selectedActivityMessageIdRef.current = selectedActivityMessageId;
  }, [selectedActivityMessageId]);
  const streamingRunIdRef = useRef<string | null>(null);
  const initialScrollFrameRef = useRef<number | null>(null);

  useEffect(() => {
    loadAllRef.current = loadAll;
    loadConversationSecretsRef.current = loadConversationSecrets;
    loadRunActivityTreeRef.current = loadRunActivityTree;
    loadOlderMessagesRef.current = loadOlderMessages;
    resetConversationDerivedStateRef.current = resetConversationDerivedState;
  }, [
    loadAll,
    loadConversationSecrets,
    loadRunActivityTree,
    loadOlderMessages,
    resetConversationDerivedState
  ]);

  useEffect(() => {
    if (!conversationId) {
      return;
    }
    resetConversationDerivedStateRef.current();
    initialScrollDoneRef.current = false;
    const timer = window.setTimeout(() => {
      setEventsError(null);
      setQueueReorderBusy(false);
      setShowConversationSecrets(false);
      setConversationSecretDraftType('env');
      setConversationSecretDraftDescription('');
      setSelectedModelId(null);
      setShellAutoExpandMessageId(null);
      setStreamState('idle');
      setShowJumpToLatest(false);
      setAutopilotMenuOpen(false);
      setExpandedRunnerSteps({});
      if (authorizationReady) {
        void loadAllRef.current();
      }
    }, 0);
    return () => {
      window.clearTimeout(timer);
    };
  }, [
    conversationId,
    authorizationReady,
    initialScrollDoneRef,
    setAutopilotMenuOpen,
    setConversationSecretDraftDescription,
    setConversationSecretDraftType,
    setEventsError,
    setExpandedRunnerSteps,
    setQueueReorderBusy,
    setSelectedModelId,
    setShellAutoExpandMessageId,
    setShowConversationSecrets,
    setShowJumpToLatest,
    setStreamState
  ]);

  useEffect(() => {
    if (conversationTitle) {
      document.title = `${conversationTitle} - Mission Control`;
    } else {
      document.title = 'Mission Control';
    }
    return () => {
      document.title = 'Mission Control';
    };
  }, [conversationTitle]);

  useEffect(() => {
    if (apiClient.mode === 'tool-browser') {
      if (selectedModelId !== null && !models.some(model => model.id === selectedModelId)) {
        setSelectedModelId(null);
      }
      return;
    }
    if (models.length === 0 || selectedModelId !== null || messages.length === 0) {
      return;
    }
    const storedModelId =
      typeof window !== 'undefined'
        ? window.localStorage.getItem('conversation.lastSelectedModelId')
        : null;
    const resolvedModelId = resolveInitialConversationModelId({
      messages,
      models,
      storedModelId
    });
    if (resolvedModelId) {
      setSelectedModelId(resolvedModelId);
    }
  }, [apiClient.mode, messages, models, selectedModelId, setSelectedModelId]);

  useEffect(() => {
    if (!showConversationSecrets) {
      return;
    }
    const targetAppId = conversationAppId ?? appId;
    if (!conversationId || !targetAppId) {
      return;
    }
    void loadConversationSecretsRef.current();
  }, [showConversationSecrets, conversationId, conversationAppId, appId]);

  useEffect(() => {
    if (!conversationId || !authorizationReady || apiClient.mode === 'tool-browser') {
      return;
    }

    let canceled = false;
    let reconnectTimer: number | null = null;
    let tokenRequestId: string | null = null;

    const handleConnectFailure = (event?: CloseEvent) => {
      if (canceled) {
        return;
      }
      const attempts = conversationWsRetryRef.current + 1;
      conversationWsRetryRef.current = attempts;
      if (attempts > 3) {
        const message = formatWebSocketClose(
          'Conversation updates disconnected',
          event,
          tokenRequestId
        );
        setEventsError(message);
        setError(message);
        setStreamState('disconnected');
        return;
      }
      const delay = Math.min(6000, 600 * attempts);
      reconnectTimer = window.setTimeout(() => {
        void connect();
      }, delay);
    };

    const handleAuthorizationLost = (detail: string) => {
      if (canceled) {
        return;
      }
      conversationWsRetryRef.current = 0;
      if (reconnectTimer) {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      setMessages([]);
      setQueue([]);
      setRuns([]);
      setRunEvents([]);
      onConversationAuthorizationLost();
      setEventsError(detail);
      setError(detail);
      setStreamState('disconnected');
    };

    const connect = async () => {
      if (canceled) {
        return;
      }

      let wsToken = '';
      try {
        const response = await apiClient.post<{ token: string; request_id?: string | null }>(
          `/api/conversations/${conversationId}/updates/ws-token`
        );
        wsToken = response?.token ?? '';
        tokenRequestId = response?.request_id ?? tokenRequestId;
      } catch (err) {
        const message = formatWebSocketTokenFailureMessage(
          'Conversation updates disconnected',
          err
        );
        console.error(message, err);
        if (isConversationAuthorizationFailure(err)) {
          handleAuthorizationLost('Conversation access was revoked. Refreshing access controls.');
        } else {
          setError(message);
          setEventsError(message);
          setStreamState('disconnected');
        }
        return;
      }

      if (canceled || !wsToken) {
        handleConnectFailure();
        return;
      }

      const url = new URL('/api/conversations/updates/ws', window.location.origin);
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      url.searchParams.set('ws_token', wsToken);

      const socket = new WebSocket(url.toString());
      conversationWsRef.current = socket;

      socket.onopen = () => {
        if (canceled) {
          socket.close();
          return;
        }
        conversationWsRetryRef.current = 0;
        socket.send(JSON.stringify({ type: 'subscribe', conversation_id: conversationId }));
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'updates' && data.conversation_id === conversationId) {
            const hasMessages = Array.isArray(data.messages) && data.messages.length > 0;
            const hasQueue = Array.isArray(data.queue);
            const hasRuns = Array.isArray(data.runs);
            if (hasMessages || hasQueue || hasRuns) {
              liveStateRevisionRef.current += 1;
            }
            if (hasMessages) {
              setMessages((prev) => {
                const merged = new Map(prev.map((message) => [message.id, message]));
                for (const message of data.messages) {
                  merged.set(message.id, message);
                }
                return Array.from(merged.values()).sort(compareConversationMessages);
              });
            }
            if (hasQueue) {
              setQueue(data.queue);
            }
            if (hasRuns) {
              setRuns(data.runs);
            }
          } else if (data.type === 'error') {
            console.error('Conversation WebSocket error:', data.error);
          }
        } catch {
          // Ignore parse errors.
        }
      };

      socket.onerror = () => {
        socket.close();
      };

      socket.onclose = (event) => {
        if (!canceled) {
          if (isConversationAuthorizationClose(event)) {
            handleAuthorizationLost('Conversation access was revoked. Refreshing access controls.');
            return;
          }
          if (event.wasClean) {
            return;
          }
          handleConnectFailure(event);
        }
      };
    };

    void connect();

    return () => {
      canceled = true;
      if (reconnectTimer) {
        window.clearTimeout(reconnectTimer);
      }
      if (conversationWsRef.current) {
        conversationWsRef.current.close();
        conversationWsRef.current = null;
      }
    };
  }, [
    apiClient,
    authorizationReady,
    conversationId,
    liveStateRevisionRef,
    onConversationAuthorizationLost,
    setError,
    setEventsError,
    setMessages,
    setQueue,
    setRunEvents,
    setRuns,
    setStreamState
  ]);

  const streamingRunId = streamingRun?.id ?? null;
  const streamingRunMessageId = streamingRun?.message_id ?? null;

  useEffect(() => {
    if (!conversationId || !canLoadRuntime || !streamingRunId || apiClient.mode === 'tool-browser') {
      runCursorRef.current = null;
      const timer = window.setTimeout(() => {
        setRunEvents([]);
        setEventsError(null);
        setApprovalDismissedKey(null);
        setRunErrorDismissedId(null);
        setStreamState('idle');
      }, 0);
      return () => {
        window.clearTimeout(timer);
      };
    }

    let canceled = false;
    let reconnectTimer: number | null = null;
    let tokenRequestId: string | null = null;

    runCursorRef.current = null;
    streamingRunIdRef.current = streamingRunId;
    const setupTimer = window.setTimeout(() => {
      setRunEvents([]);
      setEventsError(null);
      setApprovalDismissedKey(null);
      setRunErrorDismissedId(null);
    }, 0);

    if (streamingRunMessageId && selectedActivityMessageIdRef.current !== streamingRunMessageId) {
      setSelectedActivityMessageId(streamingRunMessageId);
    }
    const connect = async () => {
      if (canceled) {
        return;
      }
      setStreamState('connecting');
      let wsToken: string | null = null;
      try {
        const data = await apiClient.post<{ token: string; request_id?: string | null }>(
          `/api/conversations/${conversationId}/runs/${streamingRunId}/events/ws-token`,
          {}
        );
        wsToken = data.token;
        tokenRequestId = data.request_id ?? tokenRequestId;
      } catch (err) {
        const message = formatWebSocketTokenFailureMessage('Live run output disconnected', err);
        console.error(message, err);
        setError(message);
        setEventsError(message);
        setStreamState('disconnected');
        return;
      }
      if (canceled || !wsToken) {
        return;
      }
      const url = new URL(
        `/api/conversations/${conversationId}/runs/${streamingRunId}/events/ws`,
        window.location.origin
      );
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      if (runCursorRef.current !== null) {
        url.searchParams.set('after', String(runCursorRef.current));
      }
      url.searchParams.set('ws_token', wsToken);
      const socket = new WebSocket(url.toString());
      wsRef.current = socket;

      socket.onopen = () => {
        wsRetryRef.current = 0;
        setEventsError(null);
        setStreamState('live');
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as RunEvent;
          if (!data || typeof data.id !== 'number') {
            return;
          }
          runCursorRef.current = data.id;
          setRunEvents((prev) => [...prev, data]);
        } catch {
          // Ignore parse errors.
        }
      };

      socket.onerror = () => {
        socket.close();
      };

      socket.onclose = (event) => {
        if (canceled) {
          return;
        }
        const attempts = wsRetryRef.current + 1;
        wsRetryRef.current = attempts;
        if (attempts > 2) {
          const message = formatWebSocketClose('Live run output disconnected', event, tokenRequestId);
          setEventsError(message);
          setError(message);
          setStreamState('disconnected');
          return;
        }
        setEventsError('Live connection lost. Reconnecting…');
        setStreamState('connecting');
        const delay = Math.min(6000, 600 * attempts);
        reconnectTimer = window.setTimeout(() => {
          void connect();
        }, delay);
      };
    };

    void connect();
    return () => {
      canceled = true;
      window.clearTimeout(setupTimer);
      if (reconnectTimer) {
        window.clearTimeout(reconnectTimer);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [
    apiClient,
    canLoadRuntime,
    conversationId,
    runCursorRef,
    setApprovalDismissedKey,
    setError,
    setEventsError,
    setRunErrorDismissedId,
    setRunEvents,
    setSelectedActivityMessageId,
    setStreamState,
    streamingRunMessageId,
    streamingRunId
  ]);

  useEffect(() => {
    const effectiveMessageId = selectedActivityMessageIdRef.current;
    if (!canLoadRuntime || !showRunnerSidebar || !effectiveMessageId) {
      return;
    }
    const run = runByMessageId.get(effectiveMessageId);
    if (!run) {
      return;
    }
    const effectiveStreamingRunId = streamingRunIdRef.current;
    if (run.status === 'running' && effectiveStreamingRunId === run.id) {
      return;
    }
    if (activityTreeByMessageId[effectiveMessageId]) {
      return;
    }
    if (activityTreeLoadingByMessageId[effectiveMessageId]) {
      return;
    }
    void loadRunActivityTreeRef.current(effectiveMessageId, run.id);
  }, [
    activityTreeByMessageId,
    activityTreeLoadingByMessageId,
    canLoadRuntime,
    runByMessageId,
    showRunnerSidebar,
    streamingRunIdRef,
    selectedActivityMessageIdRef
  ]);

  useEffect(() => {
    const el = threadRef.current;
    if (!el) {
      return;
    }
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 140;
    const nextShowJumpToLatest =
      !nearBottom && Boolean(messages.length || assistantText || activeOutputEventCount || activeUpdateEventCount);
    if (nearBottom) {
      el.scrollTop = el.scrollHeight;
    }
    threadNearBottomRef.current = nearBottom;
    const frame = window.requestAnimationFrame(() => {
      setShowJumpToLatest(nextShowJumpToLatest);
    });
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [
    activeOutputEventCount,
    activeUpdateEventCount,
    assistantText,
    messages,
    setShowJumpToLatest,
    threadNearBottomRef,
    threadRef
  ]);

  useEffect(() => {
    if (initialScrollDoneRef.current || messages.length === 0) {
      return;
    }
    const el = threadRef.current;
    if (!el) {
      return;
    }
    const firstFrame = window.requestAnimationFrame(() => {
      const secondFrame = window.requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
        initialScrollDoneRef.current = true;
        threadNearBottomRef.current = true;
        setShowJumpToLatest(false);
      });
      initialScrollFrameRef.current = secondFrame;
    });
    initialScrollFrameRef.current = firstFrame;
    return () => {
      if (initialScrollFrameRef.current !== null) {
        window.cancelAnimationFrame(initialScrollFrameRef.current);
        initialScrollFrameRef.current = null;
      }
    };
  }, [initialScrollDoneRef, messages, setShowJumpToLatest, threadNearBottomRef, threadRef]);

  useEffect(() => {
    const el = threadRef.current;
    if (!el) {
      return;
    }
    const onScroll = () => {
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 140;
      const nearTop = el.scrollTop < 100;
      threadNearBottomRef.current = nearBottom;
      if (nearBottom) {
        setShowJumpToLatest(false);
      }
      if (nearTop && hasMoreMessages && !loadingOlderMessages) {
        void loadOlderMessagesRef.current();
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
    };
  }, [
    hasMoreMessages,
    loadingOlderMessages,
    setShowJumpToLatest,
    threadNearBottomRef,
    threadRef
  ]);

  useEffect(() => {
    const el = threadRef.current;
    if (!el || typeof MutationObserver === 'undefined') {
      return;
    }
    let frame: number | null = null;
    const observer = new MutationObserver(() => {
      if (!threadNearBottomRef.current) {
        return;
      }
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
      frame = window.requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
        setShowJumpToLatest(false);
        frame = null;
      });
    });
    observer.observe(el, {
      childList: true,
      characterData: true,
      subtree: true
    });
    return () => {
      observer.disconnect();
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [setShowJumpToLatest, threadNearBottomRef, threadRef]);
}
